import React, { Fragment } from 'react';
import { ButtonV3 } from '../ButtonV3';
import {
  ButtonWrapper,
  Scrim,
  StyledImage,
  StyledCardLink,
  TileWrapper,
  LinkWrapper,
  ImageElementWrapper,
  TagWrapper,
} from './CategoryTile.styles';
import { ImageDetails, getSrcSet, getSourceElements } from './imgUtils';

import { LinkV2 } from '../LinkV2';

interface ImageParameters {
  /** What image should be passed as the default src, typically the tablet size */
  featuredSrc: string;
  jpg?: ImageDetails[];
  webp?: ImageDetails[];
}

export type CategoryTileProps = {
  image?: string;
  imageFullParameters?: ImageParameters;
  altText: string;
  linkText?: string;
  href: string;
  /* Will not display if using the square variant */
  showScrim?: boolean;
  /* Variant of the card with a square image and a link below the image  */
  square?: boolean;
  /** Overrides the image element */
  imageElement?: React.ReactNode;
  /** Show a tag on the tile */
  tag?: React.ReactNode;
  tileButtonTextTransform?: boolean;
  /** Additional content shown below the tile */
  caption?: JSX.Element;
  openLinkInNewTab?: boolean;
};

const CategoryTile: React.FC<CategoryTileProps> = (props: CategoryTileProps) => {
  const {
    image,
    altText,
    linkText,
    href,
    showScrim,
    square = false,
    imageFullParameters,
    imageElement,
    tag,
    tileButtonTextTransform = true,
    caption,
    openLinkInNewTab,
  } = props;

  const renderImage = () => {
    if (imageElement) {
      return <ImageElementWrapper square={square}>{imageElement}</ImageElementWrapper>;
    }
    if (imageFullParameters) {
      return (
        <StyledImage as="picture">
          {getSourceElements(imageFullParameters.webp, 'webp')}
          {getSourceElements(imageFullParameters.jpg, 'jpg')}
          <StyledImage
            src={imageFullParameters.featuredSrc}
            srcSet={getSrcSet(imageFullParameters.jpg)}
            alt={altText || linkText}
            loading="lazy"
          />
        </StyledImage>
      );
    }
    if (image) {
      return <StyledImage src={image} alt={altText || linkText} />;
    }
    return null;
  };

  return (
    <TileWrapper square={square}>
      <StyledCardLink
        href={href}
        target={openLinkInNewTab ? '_blank' : ''}
        rel={openLinkInNewTab ? 'noopener noreferrer' : ''}
        title={linkText}
        square={square}
      >
        {renderImage()}
      </StyledCardLink>
      {tag ? <TagWrapper>{tag}</TagWrapper> : undefined}
      {linkText && (
        <Fragment>
          {square ? (
            <LinkWrapper square={square}>
              <LinkV2
                href={href}
                target={openLinkInNewTab ? '_blank' : ''}
                rel={openLinkInNewTab ? 'noopener noreferrer' : ''}
                inline
                noUnderline
              >
                {linkText}
              </LinkV2>
            </LinkWrapper>
          ) : (
            <ButtonWrapper>
              <ButtonV3
                component="a"
                href={href}
                variant="primary"
                colorway="cloudAlt"
                size="small"
                textTransform={tileButtonTextTransform}
                target={openLinkInNewTab ? '_blank' : ''}
                rel={openLinkInNewTab ? 'noopener noreferrer' : ''}
              >
                {linkText}
              </ButtonV3>
            </ButtonWrapper>
          )}
        </Fragment>
      )}
      {caption}
      {showScrim && !square && <Scrim />}
    </TileWrapper>
  );
};

export default CategoryTile;
